import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import MuxVideoPlayer from '../song-detail-page/components/MuxVideoPlayer';
import { ArrowLeft, Film, BookOpen, CheckCircle, Award, ChevronRight, ChevronDown, ChevronUp, AlignLeft } from 'lucide-react';
import { trackWatchEvent } from '../../services/videoEngagementAnalyticsService';

const LESSON_CONFIG = {
  overview: {
    label: 'Overview',
    number: 0,
    videoField: 'video_url_overview',
    icon: BookOpen,
    color: 'blue',
  },
  level1: {
    label: 'Level 1 – Interpretation',
    number: 1,
    videoField: 'video_url_level1',
    icon: BookOpen,
    color: 'indigo',
  },
  level2: {
    label: 'Level 2 – Context & Emotion',
    number: 2,
    videoField: 'video_url_level2',
    icon: BookOpen,
    color: 'violet',
  },
  level3: {
    label: 'Level 3 – Phrasing & Technique',
    number: 3,
    videoField: 'video_url_level3',
    icon: BookOpen,
    color: 'purple',
  },
  level4: {
    label: 'Level 4 – Performance Tips',
    number: 4,
    videoField: 'video_url_level4',
    icon: BookOpen,
    color: 'fuchsia',
  },
  level5: {
    label: 'Level 5 – Mastery',
    number: 5,
    videoField: 'video_url_level5',
    icon: Award,
    color: 'pink',
  },
};

export default function LessonLearningInterface() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const songId = searchParams?.get('songId');
  const roleId = searchParams?.get('roleId');
  const analysisId = searchParams?.get('analysisId');
  const lessonSection = searchParams?.get('lesson') || 'overview';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [song, setSong] = useState(null);
  const [role, setRole] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [lessonProgress, setLessonProgress] = useState(null);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [videoCompleteToast, setVideoCompleteToast] = useState(false);
  const [videoRecord, setVideoRecord] = useState(null);
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  const config = LESSON_CONFIG?.[lessonSection] || LESSON_CONFIG?.overview;

  useEffect(() => {
    if (songId && analysisId && user) {
      loadLessonData();
    }
  }, [songId, analysisId, lessonSection, user]);

  const loadLessonData = async () => {
    try {
      setLoading(true);
      setError('');
      setQuizSubmitted(false);
      setQuizAnswers({});
      setQuizScore(null);
      setVideoRecord(null);

      // Load song
      const { data: songData, error: songError } = await supabase
        ?.from('songs')
        ?.select('id, title, musicals(id, title, poster_url)')
        ?.eq('id', songId)
        ?.single();
      if (songError) throw songError;
      setSong(songData);

      // Load role
      if (roleId) {
        const { data: roleData, error: roleError } = await supabase
          ?.from('roles')
          ?.select('id, character_name, voice_type, vocal_range')
          ?.eq('id', roleId)
          ?.single();
        if (!roleError) setRole(roleData);
      }

      // Load character analysis
      const { data: analysisData, error: analysisError } = await supabase
        ?.from('character_song_analyses')
        ?.select('*')
        ?.eq('id', analysisId)
        ?.single();
      if (analysisError) throw analysisError;
      setAnalysis(analysisData);

      // Fetch captions/transcript for the current lesson video
      const playbackId = analysisData?.[LESSON_CONFIG?.[lessonSection]?.videoField || 'video_url_overview'];
      if (playbackId) {
        const { data: vidData } = await supabase
          ?.from('videos')
          ?.select('id, transcript, captions')
          ?.eq('mux_playback_id', playbackId)
          ?.eq('status', 'ready')
          ?.maybeSingle();
        setVideoRecord(vidData || null);
      }

      // Load or create lesson progress
      const { data: progressData } = await supabase
        ?.from('user_lesson_progress')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.eq('song_id', songId)
        ?.eq('character_analysis_id', analysisId)
        ?.eq('lesson_section', lessonSection)
        ?.single();

      if (progressData) {
        setLessonProgress(progressData);
      } else {
        // Create progress record as 'available'
        const { data: newProgress } = await supabase
          ?.from('user_lesson_progress')
          ?.insert({
            user_id: user?.id,
            song_id: songId,
            character_analysis_id: analysisId,
            lesson_section: lessonSection,
            lesson_status: 'available',
          })
          ?.select()
          ?.single();
        setLessonProgress(newProgress);
      }

      // Mark as started if available
      if (progressData?.lesson_status === 'available') {
        const { data: updated } = await supabase
          ?.from('user_lesson_progress')
          ?.update({ lesson_status: 'started' })
          ?.eq('id', progressData?.id)
          ?.select()
          ?.single();
        if (updated) setLessonProgress(updated);
      }

      // Load quiz questions
      const { data: quizData } = await supabase
        ?.from('lesson_quizzes')
        ?.select('id, lesson_section, song_id, character_analysis_id')
        ?.eq('song_id', songId)
        ?.eq('lesson_section', lessonSection);

      if (quizData?.length > 0) {
        const quizId = quizData?.[0]?.id;
        const { data: questions } = await supabase
          ?.from('quiz_questions')
          ?.select('*')
          ?.eq('quiz_id', quizId)
          ?.order('order_index', { ascending: true });
        setQuizQuestions(questions || []);
      }

    } catch (err) {
      console.error('Error loading lesson data:', err);
      setError(err?.message || 'Failed to load lesson');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = async () => {
    if (!lessonProgress?.id) return;
    try {
      setMarkingComplete(true);
      const { data: updated } = await supabase
        ?.from('user_lesson_progress')
        ?.update({
          lesson_status: 'completed',
          completed_at: new Date()?.toISOString(),
        })
        ?.eq('id', lessonProgress?.id)
        ?.select()
        ?.single();
      if (updated) setLessonProgress(updated);
    } catch (err) {
      console.error('Error marking complete:', err);
    } finally {
      setMarkingComplete(false);
    }
  };

  const handleVideoComplete = async () => {
    // If already completed, do nothing
    if (lessonProgress?.lesson_status === 'completed') return;
    // Auto-mark complete
    try {
      setMarkingComplete(true);
      const progressId = lessonProgress?.id;
      if (!progressId) return;
      const { data: updated } = await supabase
        ?.from('user_lesson_progress')
        ?.update({
          lesson_status: 'completed',
          completed_at: new Date()?.toISOString(),
        })
        ?.eq('id', progressId)
        ?.select()
        ?.single();
      if (updated) setLessonProgress(updated);
      setVideoCompleteToast(true);
      setTimeout(() => setVideoCompleteToast(false), 4000);
    } catch (err) {
      console.error('Error auto-marking complete:', err);
    } finally {
      setMarkingComplete(false);
    }
  };

  const handleQuizSubmit = async () => {
    if (quizQuestions?.length === 0) return;
    try {
      setSubmittingQuiz(true);
      let correct = 0;
      quizQuestions?.forEach((q) => {
        if (quizAnswers?.[q?.id] === q?.correct_answer) correct++;
      });
      const score = Math.round((correct / quizQuestions?.length) * 100);
      const isPerfect = score >= 80;
      setQuizScore(score);
      setQuizSubmitted(true);

      if (lessonProgress?.id) {
        const { data: updated } = await supabase
          ?.from('user_lesson_progress')
          ?.update({
            quiz_completed: true,
            quiz_score: score,
            is_quiz_perfect: isPerfect,
            lesson_status: 'completed',
            completed_at: new Date()?.toISOString(),
          })
          ?.eq('id', lessonProgress?.id)
          ?.select()
          ?.single();
        if (updated) setLessonProgress(updated);
      }
    } catch (err) {
      console.error('Error submitting quiz:', err);
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const handleWatchEvent = async ({ milestone, watchPercentage, watchDurationSeconds, totalDurationSeconds, completed, replayCount }) => {
    if (!song) return;
    const videoId = song?.[`video_url_${lessonSection === 'overview' ? 'overview' : lessonSection?.replace('level', 'level')}`];
    if (!videoId) return;
    await trackWatchEvent({
      videoId,
      songAnalysisId: song?.id || null,
      lessonLevel: lessonSection === 'overview' ? 'overview' : lessonSection?.replace('level', 'level_'),
      watchDurationSeconds,
      totalDurationSeconds,
      completed,
      replayCount,
      watchPercentage,
      milestone,
    });
  };

  const renderVideoSection = () => {
    const playbackId = analysis?.[config?.videoField];
    if (!playbackId) return null;

    const captions = videoRecord?.captions || [];
    const transcript = videoRecord?.transcript || '';

    return (
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Film className="w-5 h-5 text-indigo-600" />
          <h2 className="text-xl font-bold text-gray-900">
            {config?.label} – Video Lesson
          </h2>
        </div>
        <div className="rounded-2xl overflow-hidden shadow-lg max-w-3xl">
          <MuxVideoPlayer
            videoId={playbackId}
            title={`${config?.label} – ${song?.title}`}
            thumbnailUrl={song?.musicals?.poster_url || ""}
            propThumbnail={song?.musicals?.poster_url || ""}
            className="w-full"
            onVideoComplete={handleVideoComplete}
            onWatchEvent={handleWatchEvent}
            captions={captions}
          />
        </div>
        {/* Collapsible Transcript */}
        {transcript && (
          <div className="max-w-3xl mt-3 border border-gray-200 rounded-xl overflow-hidden">
            <button
              onClick={() => setTranscriptOpen(prev => !prev)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-medium text-gray-700"
            >
              <div className="flex items-center gap-2">
                <AlignLeft className="w-4 h-4 text-indigo-500" />
                <span>Transcript</span>
              </div>
              {transcriptOpen
                ? <ChevronUp className="w-4 h-4 text-gray-500" />
                : <ChevronDown className="w-4 h-4 text-gray-500" />}
            </button>
            {transcriptOpen && (
              <div className="px-5 py-4 bg-white max-h-72 overflow-y-auto">
                {captions?.length > 0 ? (
                  <div className="space-y-2">
                    {captions?.map((seg, idx) => (
                      <div
                        key={idx}
                        className={`flex gap-3 text-sm ${seg?.is_key_point ? 'bg-yellow-50 rounded-lg px-2 py-1 -mx-2' : ''}`}
                      >
                        <span className="flex-shrink-0 text-xs font-mono text-indigo-500 mt-0.5 w-10">
                          {Math.floor((seg?.timestamp_seconds || 0) / 60)}:{String(Math.floor((seg?.timestamp_seconds || 0) % 60))?.padStart(2, '0')}
                        </span>
                        <div className="flex-1">
                          <p className="text-gray-800 leading-relaxed">{seg?.text}</p>
                          {seg?.is_key_point && seg?.teaching_point && (
                            <p className="text-xs font-semibold text-yellow-700 mt-0.5">★ {seg?.teaching_point}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{transcript}</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderTextContent = () => {
    if (!analysis) return null;

    if (lessonSection === 'overview') {
      const overview = analysis?.overview || {};
      const hasContent = overview?.emotionalCore || overview?.objective ||
        overview?.vocalRange || overview?.notes || overview?.character;
      if (!hasContent) return null;

      return (
        <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-600" />
            Overview Analysis
          </h2>
          <div className="space-y-5">
            {overview?.character && (
              <div>
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Character</p>
                <p className="text-gray-900 text-lg">{overview?.character}</p>
              </div>
            )}
            {overview?.emotionalCore && (
              <div>
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Emotional Core</p>
                <p className="text-gray-800 leading-relaxed">{overview?.emotionalCore}</p>
              </div>
            )}
            {overview?.objective && (
              <div>
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Objective</p>
                <p className="text-gray-800 leading-relaxed">{overview?.objective}</p>
              </div>
            )}
            {overview?.vocalRange && (
              <div>
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Vocal Range</p>
                <p className="text-gray-800">{overview?.vocalRange}</p>
              </div>
            )}
            {overview?.notes && (
              <div>
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Notes</p>
                <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">{overview?.notes}</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (lessonSection === 'level1') {
      const level1 = analysis?.level1 || '';
      if (!level1) return null;
      return (
        <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-indigo-600" />
            Level 1 – Interpretation
          </h2>
          <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">{level1}</p>
        </div>
      );
    }

    if (lessonSection === 'level2') {
      const level2 = analysis?.level2 || '';
      if (!level2) return null;
      return (
        <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-violet-600" />
            Level 2 – Context & Emotion
          </h2>
          <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">{level2}</p>
        </div>
      );
    }

    if (lessonSection === 'level3') {
      const level3 = analysis?.level3 || [];
      const sections = Array.isArray(level3) ? level3 : [];
      if (sections?.length === 0) return null;
      return (
        <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-purple-600" />
            Level 3 – Phrasing & Technique
          </h2>
          <div className="space-y-6">
            {sections?.map((section, idx) => (
              <div key={idx} className={idx > 0 ? 'pt-6 border-t border-gray-100' : ''}>
                {section?.section && (
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{section?.section}</h3>
                )}
                {section?.header && (
                  <div className="mb-3">
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Header</p>
                    <p className="text-gray-800">{section?.header}</p>
                  </div>
                )}
                {section?.content && (
                  <div>
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Content</p>
                    <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">{section?.content}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (lessonSection === 'level4') {
      const level4 = analysis?.level4 || {};
      if (Object.keys(level4)?.length === 0) return null;
      return (
        <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-fuchsia-600" />
            Level 4 – Performance Tips
          </h2>
          <div className="space-y-6">
            {Object.entries(level4)?.map(([key, value], idx) => (
              <div key={key} className={idx > 0 ? 'pt-6 border-t border-gray-100' : ''}>
                <h3 className="text-lg font-semibold text-gray-900 capitalize mb-3">
                  {key?.replace(/([A-Z])/g, ' $1')?.trim()}
                </h3>
                {typeof value === 'string' ? (
                  <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">{value}</p>
                ) : Array.isArray(value) ? (
                  <ul className="list-disc list-inside space-y-1">
                    {value?.map((item, i) => (
                      <li key={i} className="text-gray-800">{typeof item === 'string' ? item : JSON.stringify(item)}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-800">{JSON.stringify(value)}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (lessonSection === 'level5') {
      const level5 = analysis?.level5 || {};
      const phrases = level5?.phrases || [];
      if (phrases?.length === 0) return null;
      return (
        <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Award className="w-5 h-5 text-pink-600" />
            Level 5 – Mastery Phrases
          </h2>
          <div className="space-y-6">
            {phrases?.map((phrase, idx) => (
              <div key={idx} className={`p-5 rounded-xl bg-gray-50 ${idx > 0 ? 'mt-4' : ''}`}>
                {phrase?.lyric && (
                  <p className="text-lg font-semibold text-gray-900 mb-3 italic">&ldquo;{phrase?.lyric}&rdquo;</p>
                )}
                {phrase?.analysis && (
                  <div className="mb-3">
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Analysis</p>
                    <p className="text-gray-800 leading-relaxed">{phrase?.analysis}</p>
                  </div>
                )}
                {phrase?.technique && (
                  <div>
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Technique</p>
                    <p className="text-gray-800 leading-relaxed">{phrase?.technique}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  const renderQuizSection = () => {
    if (quizQuestions?.length === 0) return null;

    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600" />
          Knowledge Check
        </h2>

        {quizSubmitted ? (
          <div className="text-center py-8">
            <div className={`text-5xl font-bold mb-3 ${
              quizScore >= 80 ? 'text-yellow-500' : quizScore >= 60 ? 'text-blue-600' : 'text-red-500'
            }`}>
              {quizScore}%
            </div>
            <p className="text-lg text-gray-700 mb-2">
              {quizScore >= 80 ? '🏆 Mastered! Excellent work!' :
               quizScore >= 60 ? '✅ Good job! Keep practicing.': 'Keep studying and try again!'}
            </p>
            {quizScore < 80 && (
              <button
                onClick={() => { setQuizSubmitted(false); setQuizAnswers({}); setQuizScore(null); }}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Retry Quiz
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {quizQuestions?.map((q, idx) => (
              <div key={q?.id} className="p-5 bg-gray-50 rounded-xl">
                <p className="font-semibold text-gray-900 mb-4">
                  {idx + 1}. {q?.question_text}
                </p>
                <div className="space-y-2">
                  {q?.options?.map((option, optIdx) => (
                    <label
                      key={optIdx}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        quizAnswers?.[q?.id] === option
                          ? 'bg-blue-100 border-2 border-blue-500' :'bg-white border-2 border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`question-${q?.id}`}
                        value={option}
                        checked={quizAnswers?.[q?.id] === option}
                        onChange={() => setQuizAnswers(prev => ({ ...prev, [q?.id]: option }))}
                        className="sr-only"
                      />
                      <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        quizAnswers?.[q?.id] === option ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                      }`}>
                        {quizAnswers?.[q?.id] === option && (
                          <span className="w-2 h-2 rounded-full bg-white" />
                        )}
                      </span>
                      <span className="text-gray-800">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <button
              onClick={handleQuizSubmit}
              disabled={Object.keys(quizAnswers)?.length < quizQuestions?.length || submittingQuiz}
              className="w-full py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submittingQuiz ? 'Submitting...' : 'Submit Quiz'}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderCompletionButton = () => {
    const isCompleted = lessonProgress?.lesson_status === 'completed';
    const hasQuiz = quizQuestions?.length > 0;

    if (isCompleted) {
      return (
        <div className="flex items-center gap-3 p-5 bg-green-50 border border-green-200 rounded-2xl mb-8">
          <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
          <div>
            <p className="font-semibold text-green-800">Lesson Completed!</p>
            {lessonProgress?.quiz_score !== undefined && lessonProgress?.quiz_score !== null && (
              <p className="text-sm text-green-700">Quiz Score: {lessonProgress?.quiz_score}%</p>
            )}
          </div>
        </div>
      );
    }

    if (hasQuiz) return null; // Quiz submission handles completion

    return (
      <button
        onClick={handleMarkComplete}
        disabled={markingComplete}
        className="w-full py-4 bg-blue-600 text-white font-semibold rounded-2xl hover:bg-blue-700 transition-colors disabled:opacity-50 mb-8 flex items-center justify-center gap-2"
      >
        <CheckCircle className="w-5 h-5" />
        {markingComplete ? 'Marking Complete...' : 'Mark Lesson as Complete'}
      </button>
    );
  };

  const renderLessonNav = () => {
    const sections = ['overview', 'level1', 'level2', 'level3', 'level4', 'level5'];
    const currentIdx = sections?.indexOf(lessonSection);
    const prevSection = currentIdx > 0 ? sections?.[currentIdx - 1] : null;
    const nextSection = currentIdx < sections?.length - 1 ? sections?.[currentIdx + 1] : null;

    const buildUrl = (section) =>
      `/lesson-interface?songId=${songId}&roleId=${roleId}&analysisId=${analysisId}&lesson=${section}`;

    return (
      <div className="flex items-center justify-between mt-4">
        {prevSection ? (
          <button
            onClick={() => navigate(buildUrl(prevSection))}
            className="flex items-center gap-2 px-5 py-3 bg-white border border-gray-200 rounded-xl hover:border-blue-400 hover:text-blue-600 transition-colors text-gray-700 font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            {LESSON_CONFIG?.[prevSection]?.label}
          </button>
        ) : <div />}
        {nextSection && (
          <button
            onClick={() => navigate(buildUrl(nextSection))}
            className="flex items-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
          >
            {LESSON_CONFIG?.[nextSection]?.label}
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Video complete toast */}
        {videoCompleteToast && (
          <div className="fixed top-6 right-6 z-50 flex items-center gap-3 bg-green-600 text-white px-5 py-3 rounded-2xl shadow-xl animate-fade-in">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span className="font-semibold text-sm">Lesson marked as complete!</span>
          </div>
        )}

        {/* Breadcrumb / Back */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Song Progress
          </button>
          {song && (
            <>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">{song?.title}</span>
            </>
          )}
          {role && (
            <>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">{role?.character_name}</span>
            </>
          )}
        </div>

        {/* Lesson Header */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                  {song?.musicals?.title}
                </span>
                {role && (
                  <span className="text-sm font-medium text-purple-600 bg-purple-50 px-3 py-1 rounded-full">
                    {role?.character_name}
                  </span>
                )}
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mt-2">{song?.title}</h1>
              <p className="text-gray-500 mt-1">{config?.label}</p>
            </div>
            {lessonProgress?.lesson_status === 'completed' && (
              <div className="flex items-center gap-1 text-green-600 bg-green-50 px-3 py-2 rounded-xl">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Completed</span>
              </div>
            )}
          </div>

          {/* Lesson tabs */}
          <div className="flex gap-2 mt-5 overflow-x-auto pb-1">
            {Object.entries(LESSON_CONFIG)?.map(([key, cfg]) => (
              <button
                key={key}
                onClick={() => navigate(
                  `/lesson-interface?songId=${songId}&roleId=${roleId}&analysisId=${analysisId}&lesson=${key}`
                )}
                className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  lessonSection === key
                    ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cfg?.number === 0 ? 'Overview' : `L${cfg?.number}`}
              </button>
            ))}
          </div>
        </div>

        {/* 1. VIDEO — always first */}
        {renderVideoSection()}

        {/* 2. TEXT CONTENT — after video */}
        {renderTextContent()}

        {/* 3. COMPLETION BUTTON — before quiz */}
        {renderCompletionButton()}

        {/* 4. QUIZ — last */}
        {renderQuizSection()}

        {/* Lesson Navigation */}
        {renderLessonNav()}
      </div>
    </div>
  );
}
